import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { BoxEditorLayout } from "../components/BoxEditorLayout";
import {
  type BoxDuplicateResult,
  type BoxMetaPatch,
  type BoxMetaResult,
  type BoxRefreshInput,
  boxArtUpload,
  boxWrite,
  type PublishVersionInput,
  type PublishVersionResult,
  refreshResultMessage,
} from "../lib/boxEditor";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { primaryStorefrontHostname } from "../lib/storefrontUrl";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * One tenant's own box. The layout is shared with the box template editor; this page
 * is the `boxes.*` half of it — env-scoped reads and writes, and the storefront
 * chrome the preview renders in.
 */
export function BoxEditorPage() {
  const { boxId, tenantId } = useParams({ strict: false }) as { boxId: string; tenantId: string };
  const navigate = useNavigate();
  const trpc = useTRPC();
  // Viewers get the full editor read-only; the server forbids their writes anyway.
  const { can } = useTenantRole();
  const queryClient = useQueryClient();
  const box = useQuery(trpc.boxes.get.queryOptions({ boxId }));

  // The preview is fetched here rather than inside itself, so the same component
  // can render a box template that has no storefront at all.
  const config = useStorefrontConfigQuery();

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.boxes.get.queryOptions({ boxId }).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
  };

  const updateMutation = useMutation(trpc.boxes.update.mutationOptions({ onSuccess: invalidate }));
  const publishMutation = useMutation(
    trpc.boxes.publishVersion.mutationOptions({ onSuccess: invalidate }),
  );
  const refreshMutation = useMutation(
    trpc.boxes.refreshBox.mutationOptions({
      onSuccess: (result) => {
        toast.success(refreshResultMessage(result));
        invalidate();
      },
    }),
  );
  const duplicateMutation = useMutation(
    trpc.boxes.duplicate.mutationOptions({
      onSuccess: (copy) => {
        invalidate();
        toast.success(`Copied as "${copy.name}"`);
        void navigate({
          to: "/tenants/$tenantId/boxes/$boxId",
          params: { tenantId, boxId: copy.id },
        });
      },
    }),
  );
  const startArtUpload = useMutation(trpc.boxes.startArtUpload.mutationOptions());
  const finishArtUpload = useMutation(trpc.boxes.finishArtUpload.mutationOptions());
  const deleteMutation = useMutation(
    trpc.boxes.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
        void navigate({ to: "/tenants/$tenantId/boxes", params: { tenantId } });
      },
    }),
  );

  return (
    <BoxEditorLayout
      box={box.data}
      isLoading={box.isLoading}
      loadError={box.error?.message ?? null}
      writes={{
        update: boxWrite<BoxMetaPatch, BoxMetaResult>(boxId, updateMutation),
        publish: boxWrite<PublishVersionInput, PublishVersionResult>(boxId, publishMutation),
        refresh: boxWrite<BoxRefreshInput>(boxId, refreshMutation),
        duplicate: boxWrite<BoxRefreshInput, BoxDuplicateResult>(boxId, duplicateMutation),
        remove: boxWrite<BoxRefreshInput>(boxId, deleteMutation),
        uploadArt: boxArtUpload(boxId, startArtUpload, finishArtUpload),
      }}
      // boxes.get is env-scoped and the boxes_env_matches_tenant CHECK means an
      // env-scoped row always has a tenant, so this route never holds a box
      // template. Templates are edited on /templates/$boxId.
      isCatalog={false}
      readOnly={!can("editor")}
      // No parentCrumb: inside TenantLayout, whose header owns the trail.
      storefront={{
        theme: config.query.data?.config.theme,
        terminology: config.query.data?.config.terminology,
        hostname: config.query.data ? primaryStorefrontHostname(config.query.data.domains) : null,
        enabled: config.query.data?.config.enabled ?? false,
        unavailable: config.query.isError,
      }}
    />
  );
}
